import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faEarthAmericas,
  faEarthAsia,
  faEarthEurope,
  faEarthOceania,
} from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { AppService } from '../../../../core/services/app.service';
import {
  SelectField,
  SelectOption,
} from '../../../../shared/components/form-fields/select-field/select-field';
import { faGlobeLight } from '../../../../shared/constants/fa-globe-light';
import { GatewayResult } from '../../../../shared/schemas/backend-api';

/** Option id for a stored host that is not in the server's region list. */
export const UNLISTED_REGION_ID = 'unlisted';

/**
 * Server region (gateway) picker for the setup wizard and App Settings. Loads
 * the available regions itself and binds the chosen gateway *host*, which is
 * what the app configuration stores. A region is never picked automatically.
 */
@Component({
  selector: 'andor-region-select',
  standalone: true,
  imports: [TranslatePipe, NgTemplateOutlet, SelectField, FaDuotoneIconComponent],
  templateUrl: './region-select.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './region-select.scss',
})
export class RegionSelect {
  /** The selected gateway host; `null` until a region is chosen. */
  readonly host = model<string | null>(null);
  /** Shows a stored host missing from the list as its own option instead of no selection. */
  readonly allowUnlisted = input(false);
  readonly required = input(false);
  /** Ignores selections and list refreshes, e.g. while the host is saving. */
  readonly disabled = input(false);
  readonly hint = input('');

  protected readonly faGlobe = faGlobeLight;
  protected readonly regionIcons: Record<string, IconDefinition> = {
    eu: faEarthEurope,
    us_central: faEarthAmericas,
    use_east: faEarthAmericas,
    us_west: faEarthAmericas,
    sa: faEarthAmericas,
    asia_ne: faEarthAsia,
    oc: faEarthOceania,
  };

  private readonly _appService = inject(AppService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal(false);
  readonly gateways = signal<GatewayResult>({});
  readonly selected = computed<SelectOption | null>(() => {
    const host = this.host();
    if (!host) return null;
    const listed = Object.entries(this.gateways()).find(([, gateway]) => gateway.host === host);
    if (listed) return { id: listed[0], name: listed[1].name };
    // Wait for the list before falling back, so a listed host doesn't flash as unlisted.
    return this.allowUnlisted() && !this.loading() ? { id: UNLISTED_REGION_ID, name: host } : null;
  });
  readonly options = computed<SelectOption[]>(() => {
    const listed = Object.entries(this.gateways()).map(([id, gateway]) => ({
      id,
      name: gateway.name,
    }));
    const selected = this.selected();
    return selected?.id === UNLISTED_REGION_ID ? [selected, ...listed] : listed;
  });
  /** A region is selected and the list has settled. */
  readonly ready = computed(() => !this.loading() && this.selected() !== null);

  constructor() {
    void this.load();
  }

  async load() {
    if (this.loading() || this.disabled()) return;
    this.loading.set(true);
    this.error.set(false);
    try {
      const result = await this._appService.getGateways();
      if (this._destroyRef.destroyed || this.disabled()) return;
      const entries = Object.entries(result).filter(
        ([, gateway]) => gateway?.name && gateway?.host
      );
      if (!entries.length) throw new Error('No available gateways');
      // Resolved against the previous list, so a pick made during the refresh counts.
      const selectedId = this.selected()?.id;
      const gateways: GatewayResult = Object.fromEntries(entries);
      this.gateways.set(gateways);
      if (!selectedId || selectedId === UNLISTED_REGION_ID) return;
      if (gateways[selectedId]) {
        // Keep the chosen region, following its host if the server changed it.
        this.host.set(gateways[selectedId].host);
      } else if (!this.allowUnlisted()) {
        // Never replace a region that disappeared with an automatic choice.
        this.host.set(null);
      }
    } catch {
      if (!this._destroyRef.destroyed) this.error.set(true);
    } finally {
      if (!this._destroyRef.destroyed) this.loading.set(false);
    }
  }

  select(option: SelectOption) {
    if (this.disabled()) return;
    const gateway = this.gateways()[option.id];
    if (gateway) this.host.set(gateway.host);
  }
}
